import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { IoClose } from 'react-icons/io5';
import { MdDragIndicator, MdLock } from 'react-icons/md';
import DiscardChanges from '../../../../../../app/common-components/DiscardChanges';
import CustomModal from '../../../../../../app/common-components/CustomModal';
import styles from './VulnerabilityPriorityModalContent.module.scss';

const itemShape = PropTypes.shape({ type: PropTypes.string.isRequired });

function LockedItem({ item, priority }) {
  return (
    <div className={`${styles.draggableItem} ${styles.lockedItem}`}>
      <MdLock className={styles.lockIcon} />
      <span className={styles.itemType}>{item.type}</span>
      <div className={styles.priorityBadge}>{priority}</div>
    </div>
  );
}

LockedItem.propTypes = {
  item: itemShape.isRequired,
  priority: PropTypes.number.isRequired
};

function DraggableItem({ item, index, priority }) {
  return (
    <Draggable draggableId={item.type} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`${styles.draggableItem}${snapshot.isDragging ? ` ${styles.isDragging}` : ''}`}
          style={provided.draggableProps.style}
        >
          <MdDragIndicator className={styles.dragHandle} />
          <span className={styles.itemType}>{item.type}</span>
          <div className={styles.priorityBadge}>{priority}</div>
        </div>
      )}
    </Draggable>
  );
}

DraggableItem.propTypes = {
  item: itemShape.isRequired,
  index: PropTypes.number.isRequired,
  priority: PropTypes.number.isRequired
};

function VulnerabilityPriorityModalContent({ initialData, onClose, onSave, lockedType }) {
  const lockedItem = lockedType ? initialData.find(item => item.type === lockedType) : null;
  const draggableInitial = initialData.filter(item => item.type !== lockedType);

  const [items, setItems] = useState(draggableInitial);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [discardChanges, setDiscardChanges] = useState(false);

  const handleDragEnd = (result) => {
    if (!result.destination) { return; }

    const newItems = Array.from(items);
    const [reorderedItem] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, reorderedItem);

    // Only mark as changed if the order actually changed
    const orderChanged = newItems.some((item, index) => item.type !== items[index].type);
    
    setItems(newItems);
    if (orderChanged) {
      setDiscardChanges(true);
    }
  };

  const handleSave = () => {
    const allItems = lockedItem ? [lockedItem, ...items] : items;
    const updatedItems = allItems.map((item, index) => ({
      ...item,
      priority: index + 1
    }));
    onSave(updatedItems);
    onClose();
  };

  const handleCancel = () => {
    if (discardChanges) {
      setShowDiscardModal(true);
    } else {
      setItems(draggableInitial);
      onClose();
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleDiscardConfirm = () => {
    setShowDiscardModal(false);
    setDiscardChanges(false);
    setItems(draggableInitial);
    onClose();
  };

  const handleDiscardCancel = () => {
    setShowDiscardModal(false);
  };

  const discardModalBody = () => (
    <DiscardChanges
      title="reorder"
      discardClick={handleDiscardConfirm}
      cancelClick={handleDiscardCancel}
    />
  );

  const priorityOffset = lockedItem ? 2 : 1;

  return (
    <div className={styles.modalContainer}>
      {/* Header */}
      <div className={styles.modalHeader}>
        <button
          onClick={handleClose}
          className={styles.closeButton}
          aria-label="Close"
        >
          <IoClose className={styles.closeIcon} />
        </button>
        <h1 className={styles.modalTitle}>
          Set Vulnerability Priority
        </h1>
        <p className={styles.modalSubtitle}>
          Drag and drop the items to define priority order (top = highest priority)
        </p>
      </div>

      {/* Priority List */}
      <div className={styles.modalBody}>
        <div className={styles.itemsList}>
          {lockedItem && <LockedItem item={lockedItem} priority={1} />}
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="vulnerability-list">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className={styles.itemsList}
                >
                  {items.map((item, index) => (
                    <DraggableItem
                      key={item.type}
                      item={item}
                      index={index}
                      priority={index + priorityOffset}
                    />
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      </div>

      {/* Footer */}
      <div className={styles.modalFooter}>
        <button
          onClick={handleCancel}
          className={styles.btnCancel}
          data-testid="cancelButton"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          className={styles.btnSave}
          data-testid="saveButton"
        >
          Save
        </button>
      </div>

      {/* Discard Modal */}
      <CustomModal
        modalOpen={showDiscardModal}
        modalBody={discardModalBody}
        modalHide={handleDiscardCancel}
        closeBtn={false}
        closeBtnFuc={handleDiscardCancel}
      />
    </div>
  );
}

export function VulnerabilityPriorityModal({ isOpen, onClose, data, onSave, lockedType }) {
  if (!isOpen) { return null; }

  return (
    <div className={styles.modalOverlay}>
      {/* Backdrop */}
      <div
        className={styles.modalBackdrop}
        onClick={onClose}
      />

      {/* Modal */}
      <div className={styles.modalWrapper}>
        <VulnerabilityPriorityModalContent
          initialData={data}
          onClose={onClose}
          onSave={onSave}
          lockedType={lockedType}
        />
      </div>
    </div>
  );
}
